import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  getZuoraCredentials,
  makeRecentPaymentMethodDefault,
  getTrialEndDate,
  updateTrialEndDate,
  getCreditCardsOnFile,
  insertZuoraPaymentPlan,
  getZuoraProducts,
  getZuoraRatePlans,
  createZuoraObjectCall,
  updateZuoraObjectCall,
  deleteZuoraObjectCall,
} from 'api/BillingApi';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { addToRootSaga } from 'store';
import { get } from 'lodash';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { selectPaymentPlanId, selectZuoraAccountId } from './selectors';

/**
 * fetchZuoraCredentials
 * Saga for fetching zuora credentials.
 * @return { void }
 */
export function* fetchZuoraCredentials() {
  try {
    const response = yield call(getZuoraCredentials);
    yield put(actions.receiveZuoraCredentials(response.data));
  } catch (error) {
    yield put(actions.receiveZuoraCredentialsError(error));
    yield put(addNegativeToast('Trouble getting Zuora credentials.'));
  }
}

/**
 * updateDefaultPaymentMethod
 * Saga for updating the most recent payment method and making it the default method.
 * @param { Object } params: Object containing the Zuora account name of the account
 *                           being updated.
 * @return { void }
 */
export function* updateDefaultPaymentMethod({ zuoraAccountName }) {
  try {
    const response = yield call(makeRecentPaymentMethodDefault, zuoraAccountName);
    yield put(actions.updateDefaultPaymentMethodSuccess(response.data));
    yield put(addPositiveToast('Payment information successfully updated!'));
  } catch (error) {
    yield put(actions.updateDefaultPaymentMethodError(error));
    yield put(addNegativeToast('Trouble updating payment information.'));
  }
}

/**
 * getTrialExpirationDate
 * Saga for retriving the trial expiration date. The saga will return null if ther is
 * no end date.
 * @return { void }
 */
export function* fetchTrialExpirationDate() {
  try {
    const response = yield call(getTrialEndDate);
    yield put(actions.getTrialEndDateSuccess(response.data));
  } catch (error) {
    yield put(actions.getTrialEndDateFailure(error));
  }
}

/**
 * updateTrialExpirationDate
 * Saga for updating the trial expiration date.
 * @param { Object } trialExpirationDate: Object containing the new expiration date.
 * @return { void }
 */
export function* updateTrialExpirationDate({ trialExpirationDate }) {
  try {
    const paymentId = yield select(selectPaymentPlanId);
    yield call(updateTrialEndDate, {
      trialEndDate: trialExpirationDate,
      paymentId,
    });
    yield put(actions.updateTrialEndDateSuccess(trialExpirationDate));
  } catch (error) {
    yield put(actions.updateTrialEndDateFailure(error));
  }
}

/**
 * getCreditCards
 * Saga for getting a list of all credit cards assocaited with Zuora account
 * @return { void }
 */
export function* getCreditCards() {
  try {
    const zuoraAccountId = yield select(selectZuoraAccountId);
    const response = yield call(getCreditCardsOnFile, zuoraAccountId);
    yield put(actions.getCreditCardsSuccess(response.data.creditCards));
  } catch (error) {
    yield put(actions.getCreditCardsFailure(error));
    yield put(
      // error could be bad network or bad information added to account
      addNegativeToast('Trouble getting your credit cards. Please refresh the page to try again.'),
    );
  }
}

/**
 * addZuoraPaymentPlan
 * Saga for inserting a Zuroa Payment Plan into Postgress.
 * @return { void }
 */
export function* addZuoraPaymentPlan({ zuoraAccount }) {
  try {
    const response = yield call(insertZuoraPaymentPlan, zuoraAccount);
    yield put(actions.addZuoraAccountSuccess(response.data));
    yield put(addPositiveToast('Payment plan successfully added!'));
  } catch (error) {
    yield put(actions.addZuoraAccountError(error));
    yield put(
      addNegativeToast(
        'Oops! We couldn’t add this plan. Check the Zuora account ID and your connection, then try again.',
      ),
    );
  }
}

/**
 * fetchZuoraProducts
 * Saga for fetching the Zuora Products in the Catalog
 * @return { void }
 */
export function* fetchZuoraProducts() {
  try {
    const { data } = yield call(getZuoraProducts);
    yield put(actions.getZuoraProductsSuccess(data.records));
  } catch (error) {
    yield put(
      addNegativeToast('Trouble fetching Products. Please check if Zuora is down, or try again.'),
    );
  }
}

/**
 * fetchZuoraRatePlans
 * Saga for fetching the RatePlans of a certain Product
 * @param { Object } action: Object containing the action, productId, pageNum, pageSize, getTotal boolean.
 * @return { void }
 */
export function* fetchZuoraRatePlans({ productId, pageNum, pageSize, getTotal }) {
  try {
    const { data } = yield call(getZuoraRatePlans, { productId, pageSize, pageNum, getTotal });
    yield put(actions.getZuoraRatePlansSuccess(data));
  } catch (error) {
    yield put(
      addNegativeToast('Trouble fetching Rateplans. Please check if Zuora is down, or try again.'),
    );
  }
}

/**
 * createZuoraObject
 * Saga for creating a Zuora Object
 * @param { Object } action: Object containing action and the information needed to create the new object.
 * @return { void }
 */
export function* createZuoraObject({ objectInfo, objectType, refresh }) {
  try {
    yield call(createZuoraObjectCall, objectInfo, objectType);
    yield put(addPositiveToast('Object Succesfully Created!'));
    yield put(actions.objectManipulationFinished());
    refresh();
  } catch (error) {
    yield put(
      addNegativeToast(
        `Error while creating object. Failed with message ${get(
          error,
          'response.data.message',
          'no message',
        )} `,
      ),
    );
    yield put(actions.objectManipulationFinished());
  }
}

/**
 * updateZuoraObject
 * Saga for updating a Zuora Object
 * @param { Object } action: Object containing action and the information needed to update the new object.
 * @return { void }
 */
export function* updateZuoraObject({ objectInfo, objectType, refresh }) {
  try {
    yield call(updateZuoraObjectCall, objectInfo, objectType);
    yield put(addPositiveToast('Object Succesfully Updated!'));
    yield put(actions.objectManipulationFinished());
    refresh();
  } catch (error) {
    yield put(
      addNegativeToast(
        `Error while updating object. Failed with message ${get(
          error,
          'response.data.message',
          'no message',
        )} `,
      ),
    );
    yield put(actions.objectManipulationFinished());
  }
}

/**
 * deleteZuoraObject
 * Saga for deleting a Zuora Object
 * @param { Object } action: Object containing action and the information needed to delete the new object.
 * @return { void }
 */
export function* deleteZuoraObject({ objectId, objectType, refresh }) {
  try {
    yield call(deleteZuoraObjectCall, objectId, objectType);
    yield put(addPositiveToast('Object Succesfully deleted!'));
    yield put(actions.objectManipulationFinished());
    refresh();
  } catch (error) {
    yield put(
      addNegativeToast(
        `Error while deleting object. Failed with message ${get(
          error,
          'response.data.message',
          'no message',
        )} `,
      ),
    );
    yield put(actions.objectManipulationFinished());
  }
}

addToRootSaga([
  takeLatest(actionTypes.UPDATE_DEFAULT_PAYMENT_METHOD_REQUEST, updateDefaultPaymentMethod),
  takeLatest(actionTypes.FETCH_TRIAL_EXPIRATION_DATE_REQUEST, fetchTrialExpirationDate),
  takeLatest(actionTypes.UPDATE_TRIAL_EXPIRATION_DATE_REQUEST, updateTrialExpirationDate),
  takeLatest(actionTypes.GET_CREDIT_CARDS_REQUEST, getCreditCards),
  takeLatest(actionTypes.INSERT_ZUORA_ACCOUNT_REQUEST, addZuoraPaymentPlan),
  takeLatest(actionTypes.FETCH_ZUORA_CREDENTIALS, fetchZuoraCredentials),
  takeLatest(actionTypes.GET_ZUORA_PRODUCTS_REQUEST, fetchZuoraProducts),
  takeLatest(actionTypes.GET_ZUORA_RATEPLANS_REQUEST, fetchZuoraRatePlans),
  takeLatest(actionTypes.CREATE_ZUORA_OBJECT_REQUEST, createZuoraObject),
  takeLatest(actionTypes.UPDATE_ZUORA_OBJECT_REQUEST, updateZuoraObject),
  takeLatest(actionTypes.DELETE_ZUORA_OBJECT_REQUEST, deleteZuoraObject),
]);
